"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listPieces, type CatalogPiece, type ListPiecesParams } from "@/lib/api/api-client";
import { CatalogCard } from "./catalog-card";
import { PieceCardSkeleton } from "./piece-card";

/**
 * La vue liste : une sélection ouverte, une recherche, ou tout le répertoire.
 *
 * # Le vrai défaut que ce composant répare
 *
 * La modale demandait `limit: 50` et n'avait aucun « voir plus ». Elle
 * annonçait « 221 morceaux » en pied de colonne et en rendait 50 atteignables :
 * **171 pièces publiées étaient hors de portée**, quel que soit le temps qu'on
 * passait à faire défiler. C'est ça, « la popin est trop petite » — pas les
 * 900 × 700 px.
 *
 * La pagination est explicite (un bouton) et non un défilement infini : un
 * chargement automatique retire à la personne le moment où elle décide qu'elle
 * n'a pas trouvé et qu'elle va chercher autrement. Le compteur reste affiché —
 * « 24 sur 221 » — pour qu'on sache toujours où l'on en est.
 *
 * # Accumulation, et remise à zéro
 *
 * Les pages s'empilent dans `items`. Tout changement de critère repart de
 * zéro — c'est `paramsKey` qui le décide, une signature stable des paramètres
 * plutôt que l'objet lui-même, qui est recréé à chaque rendu du parent et
 * relancerait la requête en boucle.
 */

const PAGE_SIZE = 24;

type Props = {
  /** Critères d'API. Un sélection passe ici sa `CatalogShelf.query` telle quelle. */
  params: ListPiecesParams;
  /** Ce que la liste montre, en une ligne. */
  title: string;
  /** Phrase d'intention d'une sélection, absente pour une recherche. */
  intent?: string;
  /** Rendu quand la requête ne renvoie rien. */
  emptyState: React.ReactNode;
  /**
   * Le total rendu par la **première** page, avec les critères qui l'ont
   * produit.
   *
   * La liste est le seul endroit qui sache combien le catalogue a répondu ; le
   * champ de recherche, lui, est le seul qui sache ce qu'on lui a demandé. D'où
   * ce rappel plutôt qu'un second appel : l'événement se compose chez
   * l'appelant, sans une requête de plus.
   *
   * Les critères repartent **avec** le total parce que l'appelant, lui, a pu
   * changer d'avis entre-temps : une frappe de plus réécrit l'URL pendant
   * l'aller-retour, et une mesure composée depuis son état courant décrirait
   * cette requête-là avec le compte de celle-ci. Ici les deux viennent de la
   * même réponse, par construction.
   */
  onResults?: (total: number, params: ListPiecesParams) => void;
  /** Un résultat de cette liste a été ouvert. */
  onResultOpened?: () => void;
};

type ListState = {
  items: CatalogPiece[];
  total: number;
  page: number;
  /** `true` tant qu'aucune page n'est arrivée pour ces critères. */
  initial: boolean;
  loading: boolean;
  error: string | null;
};

const INITIAL: ListState = {
  items: [],
  total: 0,
  page: 0,
  initial: true,
  loading: false,
  error: null,
};

export function LibraryBrowse({
  params,
  title,
  intent,
  emptyState,
  onResults,
  onResultOpened,
}: Props) {
  // Signature stable des critères. Le parent recrée l'objet à chaque rendu :
  // le prendre en dépendance relancerait la requête à chaque frappe, même
  // quand rien n'a changé.
  const paramsKey = JSON.stringify(params);
  // Relu depuis la signature pour disposer d'une référence stable par jeu de
  // critères, utilisable telle quelle en dépendance de hook.
  const query = useMemo(() => JSON.parse(paramsKey) as ListPiecesParams, [
    paramsKey,
  ]);

  const [state, setState] = useState<ListState>(INITIAL);

  // La requête en cours, pour ignorer la réponse d'une requête périmée : deux
  // frappes rapprochées peuvent revenir dans le désordre, et la page suivante
  // d'une sélection qu'on vient de quitter ne doit pas s'ajouter au suivant.
  const requestKey = useRef(paramsKey);

  /*
    Les rappels de mesure passent par une ref plutôt que par les dépendances de
    `loadPage`. Le parent les recrée à chaque rendu : les prendre en dépendance
    changerait l'identité de `loadPage`, donc celle de l'effet qui charge la
    première page, et la liste se rechargerait à chaque frappe dans le champ de
    recherche. Écrites après chaque rendu, elles sont toujours à jour au moment
    où une réponse arrive.
  */
  const measure = useRef({ onResults, onResultOpened });

  useEffect(() => {
    measure.current = { onResults, onResultOpened };
  });

  const loadPage = useCallback(
    (page: number) => {
      const key = paramsKey;
      setState((prev) => ({ ...prev, loading: true, error: null }));

      listPieces({ ...query, page, limit: PAGE_SIZE })
        .then((result) => {
          if (requestKey.current !== key) {
            return;
          }

          setState((prev) => ({
            // Page 1 remplace, les suivantes s'ajoutent.
            items: page === 1 ? result.items : [...prev.items, ...result.items],
            total: result.meta.total,
            page,
            initial: false,
            loading: false,
            error: null,
          }));

          // La première page seulement : les suivantes ne changent pas ce que
          // la recherche a trouvé, elles la déroulent. `query` est le jeu de
          // critères de *cette* requête, pas celui du rendu courant : c'est ce
          // qui garantit que la mesure décrit un seul instant.
          if (page === 1) {
            measure.current.onResults?.(result.meta.total, query);
          }
        })
        .catch((err: unknown) => {
          if (requestKey.current !== key) {
            return;
          }

          setState((prev) => ({
            ...prev,
            loading: false,
            error:
              err instanceof Error
                ? err.message
                : "Impossible de charger la liste",
          }));
        });
    },
    [paramsKey, query],
  );

  useEffect(() => {
    requestKey.current = paramsKey;
    setState(INITIAL);
    loadPage(1);
  }, [paramsKey, loadPage]);

  const hasMore = state.items.length < state.total;
  const showEmpty =
    !state.initial && !state.loading && state.error === null && state.total === 0;

  return (
    <div className="flex flex-col gap-4 pb-14">
      <header className="flex flex-col gap-1 px-1">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-text-secondary">
          {title}
        </h2>
        {intent !== undefined && (
          <p className="text-[13px] text-text-muted leading-snug">{intent}</p>
        )}
        {/*
          Annoncé aux lecteurs d'écran : sans ça, charger une page de plus ne
          produit aucun retour audible — la grille s'allonge en silence.
          `polite`, pour ne pas couper la lecture en cours.
        */}
        <p className="text-[12px] text-text-muted" aria-live="polite">
          {state.initial
            ? "Chargement…"
            : state.total === 0
              ? "Aucun morceau"
              : `${state.items.length} sur ${state.total} morceau${state.total > 1 ? "x" : ""}`}
        </p>
      </header>

      {state.error !== null && state.items.length === 0 ? (
        <ErrorState
          message={state.error}
          onRetry={() => loadPage(1)}
        />
      ) : showEmpty ? (
        emptyState
      ) : (
        <>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            }}
          >
            {state.items.map((piece) => (
              <CatalogCard
                key={piece.id}
                piece={piece}
                /*
                  Remonté avant la navigation : c'est ce qui dit qu'une
                  recherche a servi. Le titre du morceau ne remonte pas, seul
                  le fait qu'un résultat a été pris.
                */
                onOpen={() => measure.current.onResultOpened?.()}
              />
            ))}

            {state.loading &&
              Array.from({ length: state.initial ? 8 : 4 }, (_, i) => (
                <PieceCardSkeleton key={`skeleton-${i}`} />
              ))}
          </div>

          {/* L'échec d'une page *suivante* ne jette pas ce qui est déjà là. */}
          {state.error !== null && state.items.length > 0 && (
            <ErrorState
              message={state.error}
              onRetry={() => loadPage(state.page + 1)}
            />
          )}

          {hasMore && !state.loading && state.error === null && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => loadPage(state.page + 1)}
                className="h-9 px-5 rounded-lg text-[13px] font-medium text-text-primary cursor-pointer transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                style={{
                  backgroundColor: "var(--color-surface-card-hover)",
                  border: "1px solid var(--color-border-strong)",
                }}
              >
                Charger la suite ({state.total - state.items.length} restants)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <p className="text-[13px] text-err">{message}</p>
      {/*
        Un bouton et pas seulement un message : une panne réseau est le plus
        souvent passagère, et sans ce bouton la seule issue est de recharger la
        page — ce qui fait perdre la recherche en cours.
      */}
      <button
        type="button"
        onClick={onRetry}
        className="h-8 px-4 rounded-lg text-[12px] font-medium text-text-primary cursor-pointer transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{
          backgroundColor: "var(--color-surface-card-hover)",
          border: "1px solid var(--color-border-strong)",
        }}
      >
        Réessayer
      </button>
    </div>
  );
}
